import type { Campaign, Stage } from '../shared/rules.js';
import type { MenuAction } from './MenuView.js';

export type StageState = 'available' | 'completed' | 'locked';

/** First stage is always available. Stage N (N≥1) is available iff N−1 has
 *  been beaten. Already-beaten stages are 'completed' (still clickable for
 *  replay; the visual treatment differs). */
export function getStageState(
  campaign: Campaign,
  index: number,
  beatenStageIds: string[],
): StageState {
  const beaten = new Set(beatenStageIds);
  const stage = campaign.stages[index];
  if (beaten.has(stage.id)) return 'completed';
  if (index === 0) return 'available';
  if (beaten.has(campaign.stages[index - 1].id)) return 'available';
  return 'locked';
}

export function createCampaignStagesView(
  container: HTMLElement,
  campaign: Campaign,
  beatenStageIds: string[],
  onAction: (action: MenuAction) => void,
): void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen campaign-stages-screen';

  el.innerHTML = `
    <div class="campaign-header">
      <button class="menu-btn campaign-back" id="back-btn">← Campaigns</button>
      <h1 class="menu-title">${escapeHtml(campaign.name)}</h1>
      <p class="campaign-subtitle">${escapeHtml(campaign.description)}</p>
    </div>
    <div class="stage-grid"></div>
  `;

  el.querySelector('#back-btn')!.addEventListener('click', () => onAction({ type: 'view-campaigns' }));

  const grid = el.querySelector('.stage-grid')!;
  for (let i = 0; i < campaign.stages.length; i++) {
    const stage = campaign.stages[i];
    const state = getStageState(campaign, i, beatenStageIds);
    const tile = document.createElement('div');
    tile.className = `stage-tile ${state}`;
    tile.innerHTML = `
      <div class="stage-tile-number">${i + 1}</div>
      ${state === 'completed' ? '<div class="completion-badge">✓</div>' : ''}
      <h2 class="stage-tile-name">${escapeHtml(stage.name)}</h2>
      <p class="stage-tile-description">${escapeHtml(stage.description)}</p>
    `;
    if (state !== 'locked') {
      tile.classList.add('clickable');
      tile.addEventListener('click', () => onAction({
        type: 'play-stage',
        campaignId: campaign.id,
        stageId: stage.id,
      }));
    }
    grid.appendChild(tile);
  }

  container.appendChild(el);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
